// AppWordmark — the product name, drawn. The ONE place the letters live.
//
// Why this is its own component rather than markup inside AppBrandMenu: the
// wordmark is two-tone, so it is *composed* from elements
// (`Arch<span>Lang</span>`). No source line ever contains the whole string,
// which means a grep-style guard reads green no matter how it is misspelled —
// and it was misspelled four different ways: two with a colon in the middle
// (one of those also lowercasing the second half), then a wrong-case run-on
// after the colon was dropped, plus the near-miss where the accented half was
// read as a qualifier slot and produced a name the product does not have. Each
// shipped from a DIFFERENT file that had rebuilt the same markup by hand.
// (Spelled out in the guard tests, which are the one place allowed to name
// them — see __tests__/AppWordmark.test.tsx.)
//
// So the rule is: no surface composes the name itself. Everything that paints
// it — the brand menu in viewer and Studio, Studio's signed-out auth screen —
// renders this, and the rendered-DOM guard in
// __tests__/AppWordmark.test.tsx checks the output of this one component.

import { cx } from "@archlang/ui";

// The name is NOT a prop, and that is the point. While callers could pass the
// halves, `suffix="lang"` type-checked and rendered — which is precisely the
// bug that shipped to Studio. No product surface ever passed them, so they were
// pure attack surface: the only thing they enabled was misspelling the product.
// A surface that needs to say what it IS uses `tagline`.
const NAME = "Arch";
const ACCENT_HALF = "Lang";

export interface AppWordmarkProps {
  /** What this surface is, set beside the name: "Studio", "Scratch", "Preview". */
  readonly tagline?: string;
  /** "sm" = inline in a menu trigger. "lg" = a hero header, with a badged tagline. */
  readonly size?: "sm" | "lg";
  /** Extra classes on the wordmark span. */
  readonly className?: string;
}

const WORD = {
  sm: "text-[14px] font-semibold tracking-tight text-slate-100",
  lg: "text-2xl font-bold tracking-tight text-slate-100",
} as const;

const TAG = {
  sm: "ml-1.5 align-middle text-[10px] font-medium uppercase tracking-[0.18em] text-slate-500",
  lg: "rounded-md bg-arch-500/15 px-2 py-0.5 text-xs font-semibold uppercase tracking-[0.14em] text-arch-300 ring-1 ring-inset ring-arch-500/20",
} as const;

/**
 * Renders two sibling spans — wordmark, then tagline — with no wrapper, so the
 * caller's own flex row controls the gap between them.
 */
export function AppWordmark({ tagline, size = "sm", className }: AppWordmarkProps) {
  return (
    <>
      <span className={cx(WORD[size], className)}>
        {NAME}
        <span className="text-arch-400">{ACCENT_HALF}</span>
      </span>
      {tagline && <span className={TAG[size]}>{tagline}</span>}
    </>
  );
}
